<template>
  <LtTooltip
    v-if="useTrigger"
    :class="ns.b('wrapper')"
    :visible="visible"
    trigger="click"
    effect="light"
    :append-to="appendTo"
    teleported
  >
    <slot name="trigger" :visible="visible" :success="isSuccess">
      <LtButton
        block
        :class="[ns.e('button'), isSuccess && ns.em('button', 'success')]"
        :type="isSuccess ? 'success' : 'primary'"
        :size="triggerSize"
        :loading="visible && !isSuccess"
        :icon="isSuccess ? Check : null"
        style="display: flex; align-items: center"
        @click="handleTrigger"
      >
        {{ triggerText ?? (isSuccess ? textInfo.completed : textInfo.trigger) }}
      </LtButton>
    </slot>

    <template #content>
      <panel
        ref="panelRef"
        :type="type"
        :slide-target="slideTarget"
        :title="title"
        :tip="tip"
        :success-tip="successTip"
        :fail-tip="failTip"
        :image="image"
        :tolerance="tolerance"
        :canvas-size="canvasSize"
        :disabled="disabled"
        :loading="loading"
        :on-before-test="onBeforeTest"
        :texts="texts"
        :fail-limit="failLimit"
        :remote-point="remotePoint"
        :use-trigger="useTrigger"
        :visible="visible"
        :hollow-shape="hollowShape"
        :hide-delay="hideDelay"
        :is-refresh="isRefresh"
        @success-change="successChange"
        @refresh="emits('refresh')"
        @drag="emits('drag')"
        @drag-start="emits('drag-start')"
        @drag-end="emits('drag-end')"
        @fail="emits('fail')"
        @success="emits('success')"
      />
    </template>
  </LtTooltip>

  <panel
    v-else
    ref="panelRef"
    :type="type"
    :slide-target="slideTarget"
    :title="title"
    :tip="tip"
    :success-tip="successTip"
    :fail-tip="failTip"
    :image="image"
    :tolerance="tolerance"
    :canvas-size="canvasSize"
    :disabled="disabled"
    :loading="loading"
    :on-before-test="onBeforeTest"
    :texts="texts"
    :fail-limit="failLimit"
    :remote-point="remotePoint"
    :use-trigger="useTrigger"
    :visible="visible"
    :hollow-shape="hollowShape"
    :hide-delay="hideDelay"
    :is-refresh="isRefresh"
    @success-change="successChange"
    @refresh="emits('refresh')"
    @drag="emits('drag')"
    @drag-start="emits('drag-start')"
    @drag-end="emits('drag-end')"
    @fail="emits('fail')"
    @success="emits('success')"
  />
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { LtButton } from '@lit-element/components/button'
import { LtTooltip } from '@lit-element/components/tooltip'
import { useNamespace } from '@lit-element/hooks'
import { Check } from '@inte-cloud/icons-vue'
import panel from './panel.vue'
import { useSetTimeout } from './hooks/useSetTimeout'
import { captchaProps, textInfo } from './data'

defineOptions({ name: 'LtCaptcha' })

const props = defineProps(captchaProps)

const emits = defineEmits([
  'refresh',
  'drag',
  'drag-start',
  'drag-end',
  'fail',
  'success',
])

const ns = useNamespace('captcha')

const { timer } = useSetTimeout()

const visible = ref(false)
const panelRef = ref()
const isSuccess = ref(false)

function successChange(value: boolean) {
  isSuccess.value = value
  if (value && props.useTrigger && visible.value) {
    clearTimeout(timer.hideTrigger)
    timer.hideTrigger = setTimeout(() => {
      visible.value = false
    }, props.hideDelay)
  }
}

watch(
  [() => props.type, () => props.remotePoint],
  () => {
    if (
      props.type !== 'slide' &&
      props.remotePoint &&
      typeof props.onBeforeTest !== 'function'
    ) {
      // console.warn(
      //   "[lit-element:Captcha] You should specify 'on-before-test' prop to valid the captcha " +
      //     "if you are using the 'point' type in remote"
      // )
    }
  },
  { immediate: true }
)

function reset(newImage?: string | (() => Promise<string>)) {
  isSuccess.value = false
  return panelRef.value?.reset(newImage)
}

function handleTrigger() {
  if (!isSuccess.value) {
    visible.value = true
  }
}

defineExpose({
  reset,
})
</script>
